import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { todasLasImagenes, obtenerImagenConLazyLoading, precargarCategoria } from './img-productos';
import './productos.css';

// Placeholder optimizado con mejor compresión
const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DYXJnYW5kby4uLjwvdGV4dD48L3N2Zz4=';

const Productos = () => {
  // Sistema de medidas mejorado
  const medidasPorCategoria = useMemo(() => ({
    'BANCAS': ['0.55m x 0.87m', '1.00m x 0.87m', '1.50m x 0.87m', '2.00m x 0.87m'],
    'BANQUETAS': ['0.55m x 0.45m', '0.80m x 0.45m', '1m x 0.45m', '1.3m x 0.45m'],
    'BEBEROS': ['0.45m x 0.80m', '0.50m x 0.85m', '0.55m x 0.90m', '0.60m x 0.95m'],
    'ESQUINEROS': ['0.35m x 1.40m', '0.40m x 1.50m', '0.45m x 1.60m', '0.50m x 1.70m'],
    'FRUTEROS': ['0.50m x 1.20m', '0.50m x 0.80m', '0.60m x 1.00m', '0.70m x 1.20m'],
    'MESAS': ['1.5m x 0.70m', '1.20m x 0.60m', '1.20m x 0.70m', '1.70m x 0.80m'],
    'SILLAS': ['0.45m x 0.45m', '0.50m x 0.50m', '0.55m x 0.55m', '0.60m x 0.60m'],
    'SILLAS DE NIÑO': ['0.50m alto', '0.60m alto', '0.55m alto', '0.65m alto'],
    'TABURETES': ['0.50m alto', '0.60m alto', '0.70m alto', '0.80m alto'],
    'TABURETES CON ESPALDAR': ['0.60m alto', '0.70m alto', '0.80m alto', '0.90m alto'],
    'MODULARES': ['1.00m x 0.40m', '1.20m x 0.45m', '1.50m x 0.50m', '1.80m x 0.55m'],
    'PERCHEROS': ['1.20m alto', '1.50m alto', '1.80m alto', '2.00m alto'],
    'MATERIALES': ['Estándar', 'Personalizado', 'Granel', 'Por unidad']
  }), []);

  // Colores disponibles
  const coloresDisponibles = useMemo(() => [
    { nombre: 'NATURAL', equivalente: 'BLANCO', codigo: '#DEB887' },
    { nombre: 'CAFÉ CEDRO', equivalente: 'NEGRO', codigo: '#8B4513' },
    { nombre: 'ENVEJECIDO', equivalente: 'ENVEJECIDO', codigo: '#241105ff' }
  ], []);

  // Estado para los productos
  const [productos, setProductos] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [datosCliente, setDatosCliente] = useState({
    nombre: '',
    email: '',
    whatsapp: ''
  });
  const [mostrarCarrito, setMostrarCarrito] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [productoAgregado, setProductoAgregado] = useState(null);
  const [selecciones, setSelecciones] = useState({});
  const [imagenActual, setImagenActual] = useState({});
  const [categoriaFiltro, setCategoriaFiltro] = useState('TODAS');
  const [productosCargados, setProductosCargados] = useState(false);
  const [imagenesPrecargadas, setImagenesPrecargadas] = useState(new Set());

  // Referencias optimizadas para lazy loading
  const observerRef = useRef(null);
  const imageElementsRef = useRef(new Map());
  const precargaTimeoutRef = useRef(null);

  // Configurar Intersection Observer OPTIMIZADO
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute('data-src');
            if (src && !imagenesPrecargadas.has(src)) {
              // Precarga inteligente
              const preloadImg = new Image();
              preloadImg.src = src;
              preloadImg.onload = () => {
                img.src = src;
                img.removeAttribute('data-src');
                img.classList.add('cargado');
                img.classList.remove('cargando');
                setImagenesPrecargadas(prev => new Set([...prev, src]));
              };
            } else if (src) {
              img.src = src;
              img.removeAttribute('data-src');
              img.classList.add('cargado');
              img.classList.remove('cargando');
            }
            observerRef.current.unobserve(img);
          }
        });
      },
      { 
        rootMargin: '150px 0px', // Aumentado para precarga más temprana
        threshold: 0.01 // Reducido para cargar más rápido
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [imagenesPrecargadas]);

  // Precarga inteligente basada en categoría filtrada
  useEffect(() => {
    if (categoriaFiltro !== 'TODAS' && productosCargados) {
      // Precargar imágenes de la categoría actual
      precargarCategoria(categoriaFiltro);
      
      // Precargar primeras imágenes de productos visibles
      const productosCategoria = productos.filter(p => p.categoria === categoriaFiltro);
      productosCategoria.slice(0, 6).forEach(producto => {
        if (producto.imagenes && producto.imagenes.length > 0) {
          const primeraImagen = producto.imagenes[0];
          if (primeraImagen && !imagenesPrecargadas.has(primeraImagen)) {
            const img = new Image();
            img.src = primeraImagen;
            img.onload = () => {
              setImagenesPrecargadas(prev => new Set([...prev, primeraImagen]));
            };
          }
        }
      });
    }
  }, [categoriaFiltro, productos, productosCargados, imagenesPrecargadas]);

  // Observar imágenes cuando se montan
  useEffect(() => {
    if (productosCargados && observerRef.current) {
      // Observar solo imágenes que no han sido precargadas
      imageElementsRef.current.forEach((img, src) => {
        if (img && img.hasAttribute('data-src') && !imagenesPrecargadas.has(src)) {
          observerRef.current.observe(img);
        } else if (img && imagenesPrecargadas.has(src)) {
          // Si ya está precargada, cargar inmediatamente
          img.src = src;
          img.removeAttribute('data-src');
          img.classList.add('cargado');
          img.classList.remove('cargando');
        }
      });
    }
  }, [productosCargados, imagenesPrecargadas]);

  // Función CORREGIDA para clasificar productos
  const generarProductosDesdeImagenes = useCallback(() => {
    const productosGenerados = [];

    // BANCAS - 6 tipos (cada tipo tiene 2 imágenes: blanco y negro)
    for (let i = 1; i <= 6; i++) {
      const imagenesBanca = todasLasImagenes['BANCAS'] || [];
      const imagenesFiltradas = [
        imagenesBanca[(i-1)*2],     // Imagen blanca
        imagenesBanca[(i-1)*2 + 1]  // Imagen negra
      ].filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `BANCA-${i}`,
        categoria: 'BANCAS',
        nombre: `BANCA TIPO ${i}`,
        descripcion: `Banca resistente tipo ${i} para exteriores e interiores, fabricada con materiales de primera calidad.`,
        medidas: medidasPorCategoria['BANCAS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Resistente a la intemperie', 'Madera de pino tratada', 'Acabado profesional']
      });
    }

    // BANQUETAS - 4 tipos (cada tipo tiene 3 imágenes)
    for (let i = 1; i <= 4; i++) {
      const imagenesBanqueta = todasLasImagenes['BANQUETAS'] || []; // CORREGIDO: 'BANQUETAS'
      const imagenesFiltradas = imagenesBanqueta.slice((i-1)*3, i*3).filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `BANQUETA-${i}`,
        categoria: 'BANQUETAS',
        nombre: `BANQUETA TIPO ${i}`,
        descripcion: `Banqueta práctica y cómoda tipo ${i}, ideal para espacios reducidos y múltiples usos.`,
        medidas: medidasPorCategoria['BANQUETAS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Estable y durable', 'Fácil de limpiar', 'Multiusos']
      });
    }

    // BEBEROS - 2 tipos
    for (let i = 1; i <= 2; i++) {
      const imagenesBebedero = todasLasImagenes['BEBEROS'] || []; // CORREGIDO: 'BEBEROS'
      const imagenesFiltradas = imagenesBebedero.slice((i-1)*2, i*2).filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `BEBERO-${i}`,
        categoria: 'BEBEROS',
        nombre: `BEBERO TIPO ${i}`,
        descripcion: `Bebero práctico tipo ${i} para bebés, diseñado para seguridad, fácil limpieza y máximo higiene.`,
        medidas: medidasPorCategoria['BEBEROS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Seguro para bebés', 'Fácil limpieza', 'Diseño ergonómico']
      });
    }

    // ESQUINEROS - CLASIFICACIÓN CORREGIDA
    const tiposEsquinero = [
      { 
        numero: 1, 
        repisas: 3, 
        imagenes: [0, 1],
        desc: 'Compacto para espacios pequeños'
      },
      { 
        numero: 2, 
        repisas: 4, 
        imagenes: [2, 3],
        desc: 'Ideal para organización media'
      },
      { 
        numero: 3, 
        repisas: 4, 
        imagenes: [4, 5],
        desc: 'Versátil para múltiples usos'
      },
      { 
        numero: 4, 
        repisas: 5, 
        imagenes: [6, 7, 8, 9],
        desc: 'Amplio capacidad de almacenamiento'
      },
      { 
        numero: 5, 
        repisas: 6, 
        imagenes: [10, 11],
        desc: 'Máximo espacio organizacional'
      }
    ];

    tiposEsquinero.forEach(tipo => {
      const imagenesEsquinero = todasLasImagenes['ESQUINEROS'] || []; // CORREGIDO: 'ESQUINEROS'
      const imagenesFiltradas = tipo.imagenes
        .map(index => imagenesEsquinero[index])
        .filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `ESQUINERO-${tipo.numero}`,
        categoria: 'ESQUINEROS',
        nombre: `ESQUINERO ${tipo.repisas} REPISAS TIPO ${tipo.numero}`,
        descripcion: `Esquinero con ${tipo.repisas} repisas tipo ${tipo.numero}, ${tipo.desc}. Perfecto para optimizar espacios en esquinas.`,
        medidas: medidasPorCategoria['ESQUINEROS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: tipo.numero,
        repisas: tipo.repisas,
        caracteristicas: [`${tipo.repisas} repisas espaciosas`, 'Optimiza espacios muertos', 'Fácil instalación']
      });
    });

    // FRUTEROS - CLASIFICACIÓN CORREGIDA
    const tiposFrutero = [
      { 
        numero: 1, 
        repisas: 3, 
        imagenes: [0, 1],
        modelo: 'Modelo A'
      },
      { 
        numero: 2, 
        repisas: 3, 
        imagenes: [2, 3],
        modelo: 'Modelo B'
      },
      { 
        numero: 3, 
        repisas: 4, 
        imagenes: [4, 5, 6, 7],
        modelo: 'Modelo C'
      }
    ];

    tiposFrutero.forEach(tipo => {
      const imagenesFrutero = todasLasImagenes['FRUTEROS'] || []; // CORREGIDO: 'FRUTEROS'
      const imagenesFiltradas = tipo.imagenes
        .map(index => imagenesFrutero[index])
        .filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `FRUTERO-${tipo.numero}`,
        categoria: 'FRUTEROS',
        nombre: `FRUTERO ${tipo.repisas} REPISAS TIPO ${tipo.numero}`,
        descripcion: `Frutero organizador ${tipo.modelo} con ${tipo.repisas} repisas, mantiene tus frutas frescas y ordenadas.`,
        medidas: medidasPorCategoria['FRUTEROS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: tipo.numero,
        repisas: tipo.repisas,
        caracteristicas: [`${tipo.repisas} niveles organizadores`, 'Ventilación natural', 'Fácil acceso', tipo.modelo]
      });
    });

    // MESAS - 4 tipos con clasificación específica
    const tiposMesa = [
      { 
        numero: 1, 
        tipo: 'BÁSICA',
        imagenes: [0, 1, 2],
        desc: 'Mesa básica versátil'
      },
      { 
        numero: 2, 
        tipo: 'CON PARRILLA',
        imagenes: [3, 4, 5],
        desc: 'Mesa con parrilla incorporada'
      },
      { 
        numero: 3, 
        tipo: 'RECTANGULAR', 
        imagenes: [6, 7, 8],
        desc: 'Mesa rectangular espaciosa'
      },
      { 
        numero: 4, 
        tipo: 'DE ÁNGULO',
        imagenes: [9, 10, 11],
        desc: 'Mesa de ángulo para espacios'
      }
    ];

    tiposMesa.forEach(tipo => {
      const imagenesMesa = todasLasImagenes['MESAS'] || []; // CORREGIDO: 'MESAS'
      const imagenesFiltradas = tipo.imagenes
        .map(index => imagenesMesa[index])
        .filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `MESA-${tipo.numero}`,
        categoria: 'MESAS',
        nombre: `MESA ${tipo.tipo} TIPO ${tipo.numero}`,
        descripcion: `Mesa ${tipo.tipo.toLowerCase()} tipo ${tipo.numero}, ${tipo.desc}. Diseño inteligente para máximo aprovechamiento.`,
        medidas: medidasPorCategoria['MESAS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: tipo.numero,
        caracteristicas: ['Superficie resistente', 'Estructura estable', 'Múltiples usos', tipo.tipo]
      });
    });

    // SILLAS - 6 tipos CORREGIDOS
    for (let i = 1; i <= 6; i++) {
      const imagenesSilla = todasLasImagenes['SILLAS'] || []; // CORREGIDO: 'SILLAS'
      const imagenesFiltradas = [
        imagenesSilla[(i-1)*2],     // Imagen blanca
        imagenesSilla[(i-1)*2 + 1]  // Imagen negra
      ].filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `SILLA-${i}`,
        categoria: 'SILLAS',
        nombre: `SILLA TIPO ${i}`,
        descripcion: `Silla cómoda y resistente tipo ${i}, perfecta para comedor o áreas sociales.`,
        medidas: medidasPorCategoria['SILLAS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Diseño ergonómico', 'Resistente al uso', 'Confort garantizado']
      });
    }

    // SILLAS DE NIÑO - 2 tipos
    for (let i = 1; i <= 2; i++) {
      const imagenesSillaNino = todasLasImagenes['SILLAS DE NIÑO'] || [];
      const imagenesFiltradas = imagenesSillaNino.slice((i-1)*4, i*4).filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `SILLA-NIÑO-${i}`,
        categoria: 'SILLAS DE NIÑO',
        nombre: `SILLA DE NIÑO TIPO ${i}`,
        descripcion: `Silla especial tipo ${i} para niños, segura y diseñada para su tamaño.`,
        medidas: medidasPorCategoria['SILLAS DE NIÑO'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Seguridad infantil', 'Materiales no tóxicos', 'Diseño apropiado']
      });
    }

    // TABURETES - 4 tipos CORREGIDOS
    for (let i = 1; i <= 4; i++) {
      const imagenesTaburete = todasLasImagenes['TABURETES'] || []; // CORREGIDO: 'TABURETES'
      const imagenesFiltradas = imagenesTaburete.slice((i-1)*3, i*3).filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `TABURETE-${i}`,
        categoria: 'TABURETES',
        nombre: `TABURETE TIPO ${i}`,
        descripcion: `Taburete práctico tipo ${i} para múltiples usos, desde cocina hasta barra.`,
        medidas: medidasPorCategoria['TABURETES'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Estabilidad superior', 'Multiusos', 'Fácil de mover']
      });
    }

    // TABURETES CON ESPALDAR - 5 tipos CORREGIDOS
    for (let i = 1; i <= 5; i++) {
      const imagenesTabureteEspaldar = todasLasImagenes['TABURETES CON ESPALDAR'] || []; // CORREGIDO
      const imagenesFiltradas = [
        imagenesTabureteEspaldar[(i-1)*2],     // Vista frontal
        imagenesTabureteEspaldar[(i-1)*2 + 1]  // Vista perspectiva
      ].filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `TABURETE-ESPALDAR-${i}`,
        categoria: 'TABURETES CON ESPALDAR',
        nombre: `TABURETE CON ESPALDAR TIPO ${i}`,
        descripcion: `Taburete con espaldar tipo ${i} para mayor comodidad y soporte lumbar.`,
        medidas: medidasPorCategoria['TABURETES CON ESPALDAR'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Soporte lumbar', 'Confort prolongado', 'Estabilidad mejorada']
      });
    }

    // MODULARES - 4 tipos
    for (let i = 1; i <= 4; i++) {
      const imagenesModular = todasLasImagenes['MODULARES'] || [];
      const imagenesFiltradas = imagenesModular.slice((i-1)*3, i*3).filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `MODULAR-${i}`,
        categoria: 'MODULARES',
        nombre: `MODULAR TIPO ${i}`,
        descripcion: `Sistema modular tipo ${i}, versátil y adaptable para cualquier espacio.`,
        medidas: medidasPorCategoria['MODULARES'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Sistema adaptable', 'Múltiples configuraciones', 'Fácil ensamblaje']
      });
    }

    // PERCHEROS - 3 tipos
    for (let i = 1; i <= 3; i++) {
      const imagenesPerchero = todasLasImagenes['PERCHEROS'] || [];
      const imagenesFiltradas = [imagenesPerchero[i-1]].filter(img => img !== undefined);
      
      productosGenerados.push({
        id: `PERCHERO-${i}`,
        categoria: 'PERCHEROS',
        nombre: `PERCHERO TIPO ${i}`,
        descripcion: `Perchero tipo ${i} funcional y elegante, perfecto para organizar prendas.`,
        medidas: medidasPorCategoria['PERCHEROS'],
        colores: coloresDisponibles,
        imagenes: imagenesFiltradas,
        numeroTipo: i,
        caracteristicas: ['Múltiples ganchos', 'Estructura robusta', 'Diseño espacioso']
      });
    }

    // PRODUCTOS EXTRAS - MATERIALES
    const productosExtras = [
      {
        id: 'ASERRIN-1',
        categoria: 'MATERIALES',
        nombre: 'ASERRIN DE PINO',
        descripcion: 'Aserrín de pino de alta calidad, ideal para manualidades, compostaje y embalaje.',
        medidas: ['Bolsa 5kg', 'Bolsa 10kg', 'Bolsa 20kg', 'Granel por m³'],
        colores: [{ nombre: 'NATURAL', equivalente: 'NATURAL', codigo: '#DEB887' }],
        imagenes: todasLasImagenes['MATERIALES'] || [], // CORREGIDO
        numeroTipo: 1,
        caracteristicas: ['100% pino natural', 'Libre de químicos', 'Múltiples aplicaciones']
      },
      {
        id: 'TABLERO-1',
        categoria: 'MATERIALES',
        nombre: 'TABLERO ALISTONADO',
        descripcion: 'Tablero alistonado de pino - Espesor 0.018 - Cara B. Medidas: 1.22m x 2.44m',
        medidas: ['1.22m x 2.44m x 0.018m', 'Personalizado'],
        colores: coloresDisponibles,
        imagenes: todasLasImagenes['TABLERO ALISTONADO'] || [],
        numeroTipo: 1,
        caracteristicas: ['Calidad premium', 'Superficie uniforme', 'Fácil trabajabilidad']
      },
      {
        id: 'TARUGO-1',
        categoria: 'MATERIALES',
        nombre: 'TARUGO ESTRIADO',
        descripcion: 'Tarugo estriado de eucalipto de 10mm, perfecto para ensamblajes profesionales.',
        medidas: ['10mm x 30mm', '10mm x 50mm', '10mm x 100mm', 'Paquete 100 unidades'],
        colores: [{ nombre: 'NATURAL', equivalente: 'NATURAL', codigo: '#DEB887' }],
        imagenes: todasLasImagenes['TARUGO ESTRIADO'] || [],
        numeroTipo: 1,
        caracteristicas: ['Estriado profesional', 'Máxima sujeción', 'Fácil instalación']
      },
      {
        id: 'MADERA-PINO-1',
        categoria: 'MATERIALES',
        nombre: 'MADERA DE PINO SECA',
        descripcion: 'Madera de pino seca de múltiples medidas, lista para proyectos profesionales.',
        medidas: ['2" x 4" x 8\'', '1" x 6" x 10\'', '2" x 6" x 12\'', 'Medida personalizada'],
        colores: coloresDisponibles,
        imagenes: todasLasImagenes['MADERA DE PINO SECA'] || [],
        numeroTipo: 1,
        caracteristicas: ['Seca y estable', 'Sin nudos grandes', 'Lista para usar']
      }
    ];

    return [...productosGenerados, ...productosExtras];
  }, [medidasPorCategoria, coloresDisponibles]);

  // Inicializar productos con carga optimizada
  useEffect(() => {
    const cargarProductosOptimizado = async () => {
      const productosGenerados = generarProductosDesdeImagenes();
      setProductos(productosGenerados);

      // Inicializar imágenes actuales
      const iniciales = {};
      productosGenerados.forEach(producto => {
        if (producto.imagenes && producto.imagenes.length > 0) {
          iniciales[producto.id] = 0;
        }
      });
      setImagenActual(iniciales);
      
      // Precargar imágenes críticas inmediatamente
      setTimeout(() => {
        setProductosCargados(true);
      }, 50); // Reducido delay
    };

    cargarProductosOptimizado();
  }, [generarProductosDesdeImagenes]);

  // Filtrar productos por categoría
  const productosFiltrados = categoriaFiltro === 'TODAS' 
    ? productos 
    : productos.filter(producto => producto.categoria === categoriaFiltro);

  // Obtener categorías únicas
  const categorias = ['TODAS', ...new Set(productos.map(p => p.categoria))];

  // Manejar selección de medida
  const handleMedidaSeleccionada = (productoId, medida) => {
    setSelecciones(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        medida,
        color: null
      }
    }));
  };

  // Manejar selección de color
  const handleColorChange = (productoId, color) => {
    setSelecciones(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        color
      }
    }));
  };

  // Cambiar imagen del producto
  const cambiarImagen = (productoId, imagenIndex, direccion) => {
    const producto = productos.find(p => p.id === productoId);
    if (!producto || !producto.imagenes) return;

    const totalImagenes = producto.imagenes.length;
    let nuevoIndex = imagenIndex + direccion;
    
    if (nuevoIndex < 0) nuevoIndex = totalImagenes - 1;
    if (nuevoIndex >= totalImagenes) nuevoIndex = 0;

    setImagenActual(prev => ({
      ...prev,
      [productoId]: nuevoIndex
    }));
  };

  // Agregar producto al carrito
  const agregarAlCarrito = (producto) => {
    const seleccion = selecciones[producto.id] || {};
    
    if (!seleccion.medida) {
      alert('Por favor selecciona una medida para este producto');
      return;
    }
    
    if (!seleccion.color) {
      alert('Por favor selecciona un color para este producto');
      return;
    }

    const imagenIndex = imagenActual[producto.id] || 0;
    const imagenSeleccionada = producto.imagenes && producto.imagenes.length > 0 
      ? producto.imagenes[imagenIndex] 
      : null;
    
    const productoConPersonalizacion = {
      id: `${producto.id}-${seleccion.medida}-${seleccion.color.nombre}-${Date.now()}`,
      nombre: producto.nombre,
      descripcion: producto.descripcion,
      medida: seleccion.medida,
      color: seleccion.color.nombre,
      imagen: imagenSeleccionada,
      categoria: producto.categoria
    };
    
    setCarrito([...carrito, productoConPersonalizacion]);
    setProductoAgregado(producto.nombre);
    setMostrarCarrito(true);
    
    setTimeout(() => {
      setProductoAgregado(null);
    }, 3000);
  };

  // Eliminar producto del carrito
  const eliminarDelCarrito = (id) => {
    setCarrito(carrito.filter(item => item.id !== id));
  };

  // Limpiar carrito completo
  const limpiarCarrito = () => {
    setCarrito([]);
    setMostrarFormulario(false);
  };

  // Manejar cambio en los inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDatosCliente({
      ...datosCliente,
      [name]: value
    });
  };

  // Generar cotización por WhatsApp
  const generarCotizacion = () => {
    if (!datosCliente.nombre || !datosCliente.whatsapp) {
      alert('Por favor completa tu nombre y número de WhatsApp');
      return;
    }

    let mensaje = "¡Hola! Quiero solicitar una cotización para los siguientes productos:%0A%0A";
    
    carrito.forEach((item, index) => {
      mensaje += `• ${item.nombre}%0A  Medida: ${item.medida}%0A  Color: ${item.color}%0A%0A`;
    });
    
    mensaje += `Total de productos: ${carrito.length}%0A%0A`;
    mensaje += `Mis datos:%0A`;
    mensaje += `Nombre: ${datosCliente.nombre}%0A`;
    mensaje += `Email: ${datosCliente.email || 'No proporcionado'}%0A`;
    mensaje += `WhatsApp: ${datosCliente.whatsapp}`;
    
    const numeroWhatsApp = '593988024805';
    window.open(`https://wa.me/${numeroWhatsApp}?text=${mensaje}`, '_blank');
    
    setCarrito([]);
    setMostrarFormulario(false);
    setMostrarCarrito(false);
    setDatosCliente({ nombre: '', email: '', whatsapp: '' });
  };

  // Función para manejar referencia de imagen
  const manejarRefImagen = (imgElement, imagenSrc) => {
    if (imgElement && imagenSrc) {
      imageElementsRef.current.set(imagenSrc, imgElement);
      
      if (observerRef.current && imgElement.hasAttribute('data-src')) {
        observerRef.current.observe(imgElement);
      }
    }
  };

  return (
    <div className="productos-container">
      <div className="hero-section">
        <h1 className="titulo-principal">Catálogo Completo de Productos</h1>
        <p className="subtitulo">Encuentra el mueble perfecto para cada espacio con nuestra amplia variedad de diseños</p>
      </div>

      {/* Filtros por categoría */}
      <div className="filtros-categoria">
        <label>Filtrar por categoría:</label>
        <select 
          value={categoriaFiltro} 
          onChange={(e) => setCategoriaFiltro(e.target.value)}
          className="selector-categoria"
        >
          {categorias.map(categoria => (
            <option key={categoria} value={categoria}>
              {categoria} ({categoria === 'TODAS' ? productos.length : productos.filter(p => p.categoria === categoria).length})
            </option>
          ))}
        </select>
      </div>

      {/* Notificación de producto agregado */}
      {productoAgregado && (
        <div className="notificacion-agregado">
          ✓ {productoAgregado} agregado al carrito
        </div>
      )}

      {/* Carrito flotante */}
      <div className={`carrito-flotante ${mostrarCarrito ? 'expandido' : ''}`}>
        <div 
          className="carrito-icono"
          onClick={() => setMostrarCarrito(!mostrarCarrito)}
        >
          <span className="carrito-contador">{carrito.length}</span>
          🛒
        </div>
        
        <div className="carrito-contenido">
          <div className="carrito-header">
            <h2>Tu Cotización</h2>
            <div className="carrito-acciones">
              {carrito.length > 0 && (
                <button 
                  className="boton-limpiar"
                  onClick={limpiarCarrito}
                >
                  🗑️ Limpiar
                </button>
              )}
              <button 
                className="cerrar-carrito"
                onClick={() => setMostrarCarrito(false)}
              >
                ×
              </button>
            </div>
          </div>
          
          <div className="carrito-items">
            {carrito.length === 0 ? (
              <p className="carrito-vacio">El carrito está vacío</p>
            ) : (
              <>
                {carrito.map(item => (
                  <div key={item.id} className="carrito-item">
                    <div className="carrito-item-imagen">
                      {item.imagen ? (
                        <img src={item.imagen} alt={item.nombre} />
                      ) : (
                        <div className="placeholder-imagen-mini">
                          {item.nombre}
                        </div>
                      )}
                    </div>
                    <div className="carrito-item-info">
                      <span className="carrito-item-nombre">{item.nombre}</span>
                      <span className="carrito-item-detalles">
                        {item.medida} | {item.color}
                      </span>
                    </div>
                    <button 
                      onClick={() => eliminarDelCarrito(item.id)}
                      className="boton-eliminar"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <div className="carrito-resumen">
                  <strong>Total de productos: {carrito.length}</strong>
                </div>
              </>
            )}
          </div>

          {carrito.length > 0 && (
            <>
              <button 
                onClick={() => setMostrarFormulario(!mostrarFormulario)}
                className="boton-cotizar"
              >
                {mostrarFormulario ? 'Ocultar formulario' : '📝 Solicitar cotización'}
              </button>

              {mostrarFormulario && (
                <div className="formulario-cliente">
                  <h3>Datos para la cotización</h3>
                  <input
                    type="text"
                    name="nombre"
                    placeholder="Nombre completo *"
                    value={datosCliente.nombre}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Correo electrónico"
                    value={datosCliente.email}
                    onChange={handleInputChange}
                  />
                  <input
                    type="tel"
                    name="whatsapp"
                    placeholder="Número de WhatsApp *"
                    value={datosCliente.whatsapp}
                    onChange={handleInputChange}
                    required
                  />
                  <button
                    onClick={generarCotizacion}
                    className="boton-whatsapp"
                    disabled={!datosCliente.nombre || !datosCliente.whatsapp}
                  >
                    📱 Enviar cotización por WhatsApp 
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lista de productos */}
      <div className="lista-productos">
        {productosFiltrados.map(producto => {
          const imagenIndex = imagenActual[producto.id] || 0;
          const imagenes = producto.imagenes || [];
          const imagenSrc = imagenes[imagenIndex];
          const tieneImagenes = imagenes.length > 0;

          return (
            <div key={producto.id} className="producto-card">
              <div className="producto-header">
                <h3>{producto.nombre}</h3>
                <div className="producto-badges">
                  <span className="producto-categoria">{producto.categoria}</span>
                  {producto.repisas && (
                    <span className="producto-repisas">{producto.repisas} REPISAS</span>
                  )}
                </div>
              </div>

              <div className="producto-imagen-container">
                <div className="producto-imagen">
                  {imagenSrc ? (
                    <img 
                      ref={(el) => manejarRefImagen(el, imagenSrc)}
                      src={placeholderImage}
                      data-src={imagenSrc}
                      alt={producto.nombre}
                      className="imagen-producto cargando"
                      loading="lazy"
                    />
                  ) : (
                    <div className="placeholder-imagen">
                      <span>{producto.nombre}</span>
                      <small>Imagen no disponible</small>
                    </div>
                  )}
                  
                  {/* Controles de imagen */}
                  {tieneImagenes && imagenes.length > 1 && (
                    <>
                      <div className="controles-imagen">
                        <button 
                          className="control-imagen prev"
                          onClick={() => cambiarImagen(producto.id, imagenIndex, -1)}
                        >
                          ‹
                        </button>
                        <button 
                          className="control-imagen next"
                          onClick={() => cambiarImagen(producto.id, imagenIndex, 1)}
                        >
                          ›
                        </button>
                      </div>
                      
                      <div className="indicadores-imagen">
                        {imagenes.map((_, index) => (
                          <button
                            key={index}
                            className={`indicador ${index === imagenIndex ? 'activo' : ''}`}
                            onClick={() => setImagenActual(prev => ({...prev, [producto.id]: index}))}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="producto-info">
                <p className="descripcion">{producto.descripcion}</p>
                
                {/* Características */}
                {producto.caracteristicas && (
                  <div className="caracteristicas">
                    <strong>Características:</strong>
                    <ul>
                      {producto.caracteristicas.map((caract, index) => (
                        <li key={index}>✓ {caract}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Selector de medida */}
                <div className="selector-medidas">
                  <label>Medidas disponibles:</label>
                  <select 
                    value={selecciones[producto.id]?.medida || ''}
                    onChange={(e) => handleMedidaSeleccionada(producto.id, e.target.value)}
                  >
                    <option value="">Selecciona una medida</option>
                    {producto.medidas.map((medida, index) => (
                      <option key={index} value={medida}>{medida}</option>
                    ))}
                  </select>
                </div>
                
                {/* Selector de colores */}
                {selecciones[producto.id]?.medida && (
                  <div className="selector-colores">
                    <label>Colores disponibles:</label>
                    <div className="colores-opciones">
                      {producto.colores.map((color, index) => (
                        <div 
                          key={index} 
                          className={`color-opcion ${selecciones[producto.id]?.color?.nombre === color.nombre ? 'seleccionado' : ''}`}
                          onClick={() => handleColorChange(producto.id, color)}
                        >
                          <div 
                            className="color-muestra" 
                            style={{ backgroundColor: color.codigo }}
                          ></div>
                          <span>{color.nombre}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <button 
                  onClick={() => agregarAlCarrito(producto)}
                  className="boton-agregar"
                  disabled={!selecciones[producto.id]?.medida || !selecciones[producto.id]?.color}
                >
                  🛒 Agregar a cotización
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {productosFiltrados.length === 0 && (
        <div className="sin-productos">
          <p>No se encontraron productos en esta categoría.</p>
        </div>
      )}
    </div>
  );
};

export default Productos;